import React from 'react';
import { Redirect, useHistory, useLocation } from 'react-router-dom';
import { ServicesContext } from '~/services';
import { Form, FormGroup, ValidationMessage } from '~/components/Form';
import { Card } from '~/components/Card';
import { Spinner } from 'react-bootstrap';
import { enumValue } from '~/util';

interface LoginProps {
    auth: TODO_TYPEME; // will come from Auth conversion
}

export function Login({ auth }: LoginProps) {
    const servicesContext = React.useContext(ServicesContext);
    const history = useHistory();
    const location = useLocation<{ from: { pathname: string } }>();

    const [email, setEmail] = React.useState('');
    const [loginError, setLoginError] = React.useState<string>();
    const [password, setPassword] = React.useState('');
    const [loadingLogin, setLoadingLogin] = React.useState(false);
    const [loadingOHID, setLoadingOHID] = React.useState(false);

    const getGenericLoginError = (code: string) =>
        `Error code: ${code}. Please contact the support team at HelpDesk@wellmed.net or 1-877-435-7576`;

    React.useEffect(() => {
        const queryParams = new URLSearchParams(location.search);
        const responseCode = queryParams.get('response-code');
        const connectionError = enumValue(['response', 'code', 'OIDC_CONNECTION_ERROR']);
        const missingCodeError = enumValue(['response', 'code', 'OIDC_AUTH_CODE_NOT_SENT_ERROR']);
        if (responseCode === String(connectionError)) {
            setLoginError(
                `Error code: ${connectionError}. Sorry, an error occurred while connecting to One Healthcare ID. Please try again. If this problem persists, please contact One Healthcare ID support.`
            );
        } else if (responseCode === String(missingCodeError)) {
            setLoginError(
                `Error code: ${missingCodeError}. You’ve been redirected here from One Healthcare ID. To access Zephyr, sign in with your email and password or select Agree & Continue with One Healthcare ID.`
            );
        } else if (responseCode) {
            setLoginError(getGenericLoginError(responseCode));
        }
    }, [location.search]);

    async function handleSubmit() {
        try {
            setLoadingLogin(true);
            await servicesContext.user.login({ email, password });
            setLoadingLogin(false);
            const { from } = location.state || { from: { pathname: '/dashboard' } };
            history.push(from.pathname);
        } catch (error) {
            setLoadingLogin(false);
            if (error.message === 'mfa') {
                history.push('/mfa');
            } else if (error.message === 'mfa-enrollment') {
                history.push('/mfa-enrollment');
            } else if (error.message === 'expired') {
                history.push(`/reset/expired/${error.code}`);
            } else {
                let errorMsg = 'Invalid username and password combination.';
                // Show x-response-code if it exists (for easier support)
                const responseCode = error?.response?.headers?.['x-response-code'];
                if (responseCode) {
                    errorMsg = getGenericLoginError(responseCode);
                }

                setLoginError(errorMsg);
            }
        }
    }

    function ohidLogin() {
        setLoadingOHID(true);
        window.open('/oidc', '_self');
    }

    function getRedirects() {
        if (window.app.reset_password && window.app.reset_password.ready) {
            servicesContext.user.setResetEndpoint(window.app.reset_password.endpoint);
            return <Redirect to="/reset" />;
        }
        if (auth.authenticated) {
            return <Redirect to="/dashboard" />;
        }
        return false;
    }

    return (
        getRedirects() || (
            <Card title="Sign In">
                <Form onSubmit={handleSubmit} values={{ email, password, loginError }}>
                    <FormGroup htmlFor="email" className="mb-3 row">
                        <label htmlFor="email" className="col-3 col-form-label">
                            Email
                        </label>
                        <div className="col-9">
                            <input
                                type="email"
                                required
                                className="form-control"
                                id="email"
                                value={email}
                                onChange={(e) => setEmail(e.target.value)}
                            />
                        </div>
                        <ValidationMessage type="required" className="offset-3 col-9">
                            Email is required.
                        </ValidationMessage>
                        <ValidationMessage type="email" className="offset-3 col-9">
                            Please enter a valid email.
                        </ValidationMessage>
                    </FormGroup>
                    <FormGroup htmlFor="password" className="mb-3 row">
                        <label htmlFor="password" className="col-3 col-form-label">
                            Password
                        </label>
                        <div className="col-9">
                            <input
                                type="password"
                                required
                                className="form-control"
                                id="password"
                                autoComplete="off"
                                value={password}
                                onChange={(e) => setPassword(e.target.value)}
                            />
                        </div>
                        <ValidationMessage type="required" className="offset-3 col-9">
                            Password is required.
                        </ValidationMessage>
                        <ValidationMessage
                            type="external"
                            rule={!!loginError}
                            className="offset-3 col-9"
                            showBeforeSubmit
                        >
                            {loginError}
                        </ValidationMessage>
                    </FormGroup>
                    <div className="row">
                        <div className="col text-end">
                            <button
                                type="submit"
                                className="btn btn-primary"
                                disabled={loadingLogin}
                            >
                                {loadingLogin && (
                                    <Spinner
                                        animation="border"
                                        variant="light"
                                        size="sm"
                                        role="status"
                                        className="me-1"
                                    />
                                )}
                                Agree and Sign In
                            </button>
                        </div>
                    </div>
                    {servicesContext.user.hasFeatureToggle('one_healthcare_id') && (
                        <div className="row">
                            <div className="col text-center">
                                <div className="separator mt-2 mb-2">Or</div>
                                <button
                                    type="button"
                                    className="btn btn-info w-100"
                                    disabled={loadingOHID}
                                    onClick={ohidLogin}
                                >
                                    {loadingOHID && (
                                        <Spinner
                                            animation="border"
                                            variant="light"
                                            size="sm"
                                            role="status"
                                            className="me-1"
                                        />
                                    )}
                                    Agree & Continue with One Healthcare ID
                                </button>
                            </div>
                        </div>
                    )}
                </Form>
            </Card>
        )
    );
}
